/*
 * Project: Dynamedics Portal Web
 * Created Date:  Friday, August 20th 2021, 5:24:24 am
 * Author: senath999
 * -----
 * Last Modified: September  1st 2021 10:21:30 pm
 * Modified By: Dinusha Madhuranga at <dmadhuranga@mitrai.com>
 * Modified By:  Senath Weerasinghe at <sweerasinghe@mitrai.com>
 * -----
 * Copyright (c) 2021 Mitra Sparks
 * -----
 * HISTORY:
 */

import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
import { reject } from 'lodash';
import { add, set, sub } from 'date-fns';
import { getBase64FromUrl } from '../../utils/convertBase64';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isReportLoading: false,
  isCalendarLoading: false,
  isReasonLoading: false,
  error: false,
  singleInterestGroup: {},
  initialInterestGroup: {
    id: '',
    name: '',
    description: '',
    image: '',
    clientPillarId: ''
  },
  organizationalPillars: [],
  interestGroups: [],
  topPosts: [],
  reportedPosts: [],
  topActivities: [],
  events: [],
  reportingReasons: [],
  isOpenModal: false,
  selectedEventId: null,
  selectedRange: null,
  reportedId: -1,
  eventSuccess: false
};

const slice = createSlice({
  name: 'interestGroups',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    //START CALENDAR LOADING
    startCalendarLoading(state) {
      state.isCalendarLoading = true;
    },
    //START Reason LOADING
    startReasonLoading(state) {
      state.isReasonLoading = true;
    },
    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isCalendarLoading = false;
      state.error = action.payload;
    },
    //INITIAL INTEREST GROUP
    initialInterestGroup(state, action) {
      state.isLoading = false;
      state.singleInterestGroup = state.initialInterestGroup;
      state.isEditMode = false;
    },

    // GET INTEREST GROUPS
    getInterestGroupsSuccess(state, action) {
      state.isLoading = false;
      state.interestGroups = action.payload;
    },

    //GET A SINGLE INTEREST GROUP
    getSingleInterestGroupSuccess(state, action) {
      state.singleInterestGroup = action.payload;
    },

    //GET A EDIT INTEREST GROUP
    getEditInterestGroupSuccess(state, action) {
      state.isLoading = false;
      state.singleInterestGroup = action.payload;
      state.isEditMode = true;
    },

    //GET TOP POSTS
    getTopPostsSuccess(state, action) {
      state.topPosts = action.payload;
    },

    //GET REPORTED POSTS
    getReportedPostsSuccess(state, action) {
      state.reportedPosts = action.payload;
    },

    //GET TOP ACTIVITIES
    getTopActivitiesSuccess(state, action) {
      state.isLoading = false;
      state.topActivities = action.payload;
    },

    // GET ORGANIZATIONAL PILLARS
    getOrganizationalPillarsSuccess(state, action) {
      state.isLoading = false;
      state.organizationalPillars = action.payload;
    },

    //DELETE REPORTED POST
    deleteReportedPostSuccess(state, action) {
      const { postId } = action.payload;
      const newReportedPosts = reject(state.reportedPosts, {
        id: postId
      });
      state.reportedPosts = newReportedPosts;
    },

    // DELETE INTEREST GROUP
    deleteInterestGroupSuccess(state, action) {
      const { interestGroupId } = action.payload;
      const deleteInterestGroup = reject(state.interestGroups, {
        id: interestGroupId
      });
      state.isLoading = false;
      state.interestGroups = deleteInterestGroup;
    },

    //GET REPORTING REASONS
    getReportingReasonsSuccess(state, action) {
      state.reportingReasons = action.payload.res;
      state.reportedId = action.payload.id;
      state.isReasonLoading = false;
    },

    // GET EVENTS
    getEventsSuccess(state, action) {
      let data = action.payload;
      let pushedEvents = data.map((item) => {
        return {
          id: item.id,
          title: item.name,
          description: item.description,
          textColor: item.colour,
          start: new Date(item.startDate),
          end: new Date(item.endDate)
        };
      });
      state.events = [...pushedEvents];
      state.isCalendarLoading = false;
    },

    //CREATE EVENT
    createEventsSuccess(state, action) {
      state.eventSuccess = true;
      state.isCalendarLoading = false;
    },

    //UPDATE EVENT
    updateEventSuccess(state, action) {
      state.isCalendarLoading = false;
    },
    //DELETE EVENT
    deleteEventSuccess(state, action) {
      const { eventId } = action.payload;
      const newEvents = reject(state.events, { id: eventId });
      state.isCalendarLoading = false;
      state.events = newEvents;
    },

    //SELECT EVENT
    selectEvent(state, action) {
      const eventId = action.payload;
      state.isOpenModal = true;
      state.selectedEventId = eventId;
    },

    // SELECT RANGE
    selectRange(state, action) {
      const { start, end } = action.payload;
      state.isOpenModal = true;
      state.selectedRange = { start, end };
    },

    // OPEN MODAL
    openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.selectedEventId = null;
      state.selectedRange = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  openModal,
  closeModal,
  selectEvent,
  getEventsSuccess,
  reasons
} = slice.actions;
// ----------------------------------------------------------------------

export function getInterestGroups() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/interest-groups');

      dispatch(slice.actions.getInterestGroupsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getInterestGroup(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/interest-groups/${id}`);
      dispatch(slice.actions.getSingleInterestGroupSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getTopPosts(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/interest-groups/${id}/posts`);
      dispatch(slice.actions.getTopPostsSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getReportedPosts(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/interest-groups/${id}/reported-posts`);
      dispatch(slice.actions.getReportedPostsSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getTopActivites(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/interest-groups/top-activities/${id}`);
      dispatch(slice.actions.getTopActivitiesSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteReportedPost(postId) {
  return async (dispatch) => {
    try {
      const response = await axios.delete(
        `/interest-groups/reported-post/${postId}`
      );
      dispatch(slice.actions.deleteReportedPostSuccess({ postId }));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getOrganizationalPillars() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/settings/pillars`);
      dispatch(
        slice.actions.getOrganizationalPillarsSuccess(response.data.data)
      );
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
// ----------------------------------------------------------------------

export function getEvents(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startCalendarLoading());
    try {
      const response = await axios.get(`/interest-groups/${id}/event`);
      dispatch(slice.actions.getEventsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export async function createInterestGroups(data) {
  let dataSet = { ...data };
  dataSet.image = await getBase64FromUrl(dataSet.image.preview);
  try {
    const response = await axios.post(`/interest-groups`, {
      ...dataSet
    });
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export async function updateInterestGroup(updatedInterestGroup) {
  let dataSet = { ...updatedInterestGroup };
  try {
    dataSet.image = dataSet.image.file
      ? await getBase64FromUrl(dataSet.image.preview)
      : null;

    const response = await axios.put(`/interest-groups/${dataSet.id}`, {
      ...dataSet
    });
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export function deleteInterestGroup(interestGroupId) {
  return async (dispatch) => {
    try {
      const response = await axios.delete(
        `/interest-groups/${interestGroupId}`
      );
      dispatch(slice.actions.deleteInterestGroupSuccess({ interestGroupId }));
      return response;
    } catch (error) {
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function setInitialInterestGroup() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.initialInterestGroup());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
// ----------------------------------------------------------------------

export function updateEvent(eventId, updateEvent) {
  return async (dispatch) => {
    dispatch(slice.actions.startCalendarLoading());
    try {
      const response = await axios.put(`/interest-groups/event/${eventId}`, {
        ...updateEvent
      });

      dispatch(slice.actions.updateEventSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function createEvent(interestGroupId, newEvent) {
  return async (dispatch) => {
    dispatch(slice.actions.startCalendarLoading());
    try {
      const response = await axios.post(
        `/interest-groups/${interestGroupId}/event`,
        { ...newEvent }
      );
      dispatch(slice.actions.createEventsSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError());
      return error;
    }
  };
}
// ----------------------------------------------------------------------

export function getEditInterestGroup(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/interest-groups/${id}`);
      dispatch(slice.actions.getEditInterestGroupSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
// ----------------------------------------------------------------------

export function deleteEvent(eventId) {
  return async (dispatch) => {
    dispatch(slice.actions.startCalendarLoading());
    try {
      const response = await axios.delete(`/interest-groups/event/${eventId}`);
      dispatch(slice.actions.deleteEventSuccess({ eventId }));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function selectRange(start, end) {
  return async (dispatch) => {
    dispatch(
      slice.actions.selectRange({
        start: start.getTime(),
        end: end.getTime()
      })
    );
  };
}

// ----------------------------------------------------------------------

export function getReasonsForReporting(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startReasonLoading());
      const response = await axios.get(
        `/interest-groups/reported-remarks/${id}`
      );
      let data = { res: [...response.data.data], id };
      dispatch(slice.actions.getReportingReasonsSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
